import React, { useState } from 'react';
import { Icon } from '../../../design/Icon';
import { fmt } from '../../../design/format';
import { textRuns } from './callDetailModel';

function TurnText({ text, marks, activeMark, onMarkEnter, onMarkLeave, onMarkClick }) {
  return textRuns(text, marks).map((run, i) => {
    if (run.kind === 'redaction')
      return (
        <span key={i} className="redact" title="Redacted before saving">
          {run.text}
        </span>
      );
    if (run.kind === 'mark')
      return (
        <mark
          key={i}
          className={`ev${activeMark === run.id ? ' on' : ''}`}
          data-ev={run.id}
          tabIndex={0}
          title="Evidence for AI analysis"
          onMouseEnter={() => onMarkEnter(run.id)}
          onMouseLeave={() => onMarkLeave(run.id)}
          onFocus={() => onMarkEnter(run.id)}
          onBlur={() => onMarkLeave(run.id)}
          onClick={() => onMarkClick(run.id)}
          onKeyDown={(e) => e.key === 'Enter' && onMarkClick(run.id)}
        >
          {run.text}
        </mark>
      );
    return <React.Fragment key={i}>{run.text}</React.Fragment>;
  });
}

/**
 * Diarized transcript with redactions and evidence marks (mockup 13-call-detail.html). Timestamps seek the player;
 * marks highlight and scroll to the AI fields they support. Non-English calls show the English translation under
 * each turn, with a toggle to read the original only.
 */
export function TranscriptCard({ subtitle, turns, marks, currentIndex, targetIndex, translated, activeMark, onSeek, onMarkEnter, onMarkLeave, onMarkClick, children }) {
  const [query, setQuery] = useState('');
  const [showEnglish, setShowEnglish] = useState('both');
  const q = query.trim().toLowerCase();

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <div className="t">Transcript</div>
          <div className="d">
            {subtitle}
            {translated && (
              <>
                {' · '}
                <button
                  type="button"
                  className="link-button"
                  aria-pressed={showEnglish === 'both' ? 'true' : 'false'}
                  onClick={() => setShowEnglish((v) => (v === 'both' ? 'original' : 'both'))}
                >
                  {showEnglish === 'both' ? 'Original only' : 'Show English'}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="acts">
          {turns && (
            <label className="global-search" style={{ maxWidth: 220 }}>
              <span className="sr-only">Search transcript</span>
              <Icon name="search" />
              <input type="search" placeholder="Search transcript" id="txq" style={{ height: 30 }} value={query} onChange={(e) => setQuery(e.target.value)} />
            </label>
          )}
        </div>
      </div>
      <div className="card-body">
        {children}
        {turns && (
          <div className="tx" id="tx">
            {turns.map((turn, i) => {
              const haystack = `${turn.text || ''} ${turn.translation || ''} ${turn.speaker?.name || ''}`.toLowerCase();
              const dim = q && !haystack.includes(q) ? ' dim' : '';
              if (turn.kind === 'system')
                return (
                  <div key={`s-${i}`} className={`turn sys${dim}`}>
                    <span className="ts num">{fmt.mmss(turn.t)}</span>
                    <div className="line">{turn.text}</div>
                  </div>
                );
              return (
                <div key={`t-${turn.index}`} className={`turn${currentIndex === turn.index ? ' current' : ''}${dim}`} data-t={turn.t} data-testid="transcript-segment" aria-current={targetIndex === turn.index ? 'true' : undefined}>
                  <button className="ts" type="button" onClick={() => onSeek(turn.t)} aria-label={`Play from ${fmt.mmss(turn.t)}`}>
                    {fmt.mmss(turn.t)}
                  </button>
                  <div>
                    <div className="sp">
                      <i style={{ background: turn.speaker?.color }} />
                      {turn.speaker?.name}
                      {turn.speaker?.isCaller && (
                        <>
                          {' '}
                          <span className="caption" style={{ fontWeight: 400 }}>
                            Caller
                          </span>
                        </>
                      )}
                    </div>
                    <div className="tt">
                      <TurnText text={turn.text} marks={marks[turn.index]} activeMark={activeMark} onMarkEnter={onMarkEnter} onMarkLeave={onMarkLeave} onMarkClick={onMarkClick} />
                    </div>
                    {turn.translation && showEnglish === 'both' && <div className="tr">{turn.translation}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
